import { useState, useEffect } from 'react';
import { Heart, Lock, Eye, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import API_URL from '../config/api';

export default function WhoLikedYou({ onUpgradeClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    fetchLikes();
  }, []);

  const fetchLikes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/likes-received`, { withCredentials: true });
      setData(response.data);
    } catch (err) {
      // Non-critical: component renders empty state when likes fail to load
      setLikes([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-[#6E1A24] animate-spin" />
      </div>
    );
  }

  if (!data || data.total_count === 0) return null;

  const canSee = data.can_see;
  const count = data.total_count;

  return (
    <div className="mb-8" data-testid="who-liked-you-section">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#6E1A24]" fill="#6E1A24" />
          <h2 className="text-lg font-serif font-medium text-[#2D2D2A]">
            {canSee ? t('matches.who_liked_title') : (count === 1 ? t('matches.someone_likes', { count }) : t('matches.someone_likes_plural', { count }))}
          </h2>
        </div>
        <span className="text-sm font-semibold bg-[#6E1A24] text-white rounded-full w-7 h-7 flex items-center justify-center">
          {count}
        </span>
      </div>

      {/* ── Blurred state for free users ── */}
      {!canSee ? (
        <button
          onClick={() => onUpgradeClick?.('who_liked')}
          className="w-full text-left group"
          data-testid="who-liked-locked-card"
        >
          <div className="relative overflow-hidden rounded-2xl border border-[#E5E3DB] bg-white transition-shadow group-hover:shadow-lg group-hover:border-[#C97A63]/40">
            {/* Blurred profile row — tappable curiosity trigger */}
            <div className="p-4 pb-3">
              <div className="flex gap-3">
                {data.profiles.slice(0, 4).map((profile) => (
                  <div key={profile.user_id || profile.name_initial} className="flex-shrink-0 relative" data-testid={`blurred-like-${profile.user_id || profile.name_initial}`}>
                    <div className="w-[72px] h-[72px] rounded-2xl overflow-hidden relative bg-gradient-to-br from-[#C97A63]/30 to-[#6E1A24]/20">
                      {/* Layered blur effect */}
                      <div className="absolute inset-0 backdrop-blur-2xl flex items-center justify-center">
                        <span className="text-3xl font-serif text-white/60 font-light select-none">{profile.name_initial}</span>
                      </div>
                      {/* Pulsing dot */}
                      <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-[#C97A63]">
                        <div className="absolute inset-0 rounded-full bg-[#C97A63] animate-ping opacity-60" />
                      </div>
                    </div>
                    <p className="text-[11px] text-[#2D2D2A] text-center mt-1.5 truncate w-[72px] font-medium">
                      {profile.first_name}{profile.age ? `, ${profile.age}` : ''}
                    </p>
                  </div>
                ))}

                {/* "More" indicator */}
                {count > data.profiles.length && (
                  <div className="flex-shrink-0 w-[72px] h-[72px] rounded-2xl bg-[#6E1A24]/5 flex flex-col items-center justify-center border border-dashed border-[#6E1A24]/20">
                    <span className="text-lg font-bold text-[#6E1A24]">+{count - data.profiles.length}</span>
                    <span className="text-[9px] text-[#6C6C67]">{t('matches.more')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* CTA banner — points to Gold */}
            <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#D4AF37]/[0.06] to-[#C97A63]/[0.06] border-t border-[#E5E3DB]">
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/15 transition-colors">
                <Lock className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2D2D2A]">
                  {count === 1 ? t('matches.interested') : t('matches.interested_plural', { count })}
                </p>
                <p className="text-xs text-[#6C6C67]">{t('matches.upgrade_gold_reveal')}</p>
              </div>
              <div className="flex-shrink-0 bg-[#D4AF37] text-white rounded-full px-3.5 py-1.5 text-xs font-semibold group-hover:bg-[#C09A2F] transition-colors flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" />
                {t('matches.gold_reveal')}
              </div>
            </div>
          </div>
        </button>
      ) : (
        /* ── Full reveal for Premium/Gold ── */
        <div className="space-y-3">
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 no-scrollbar">
            {data.profiles.map((profile) => (
              <div
                key={profile.user_id}
                className="flex flex-col items-center gap-2 flex-shrink-0"
                data-testid={`liked-profile-${profile.user_id}`}
              >
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-[#C97A63] ring-offset-2">
                    <img
                      src={getPhotoUrl(profile)}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#C97A63] rounded-full flex items-center justify-center">
                    <Heart className="w-3 h-3 text-white" fill="white" />
                  </div>
                </div>
                <span className="text-sm font-medium text-[#2D2D2A] max-w-[80px] truncate">
                  {profile.nickname || profile.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getPhotoUrl(profile) {
  if (profile?.primary_photo?.photo_id) {
    return `${API_URL}/api/photos/${profile.primary_photo.photo_id}?auth=placeholder`;
  }
  if (profile?.google_picture) {
    return profile.google_picture;
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.name || 'U')}&background=6E1A24&color=fff&size=100`;
}
